/**
 * Pure label logic for the Inbox panel header badges (SC-3392 round 12 UAT fix): the header shows
 * `api.inbox.counts` beside the "INBOX" title, readably labelled ("3 notifications", "2 tasks"), and
 * hides a badge when its count is 0. Extracted so it has proof — `index.tsx` is outside `src/ui`, so a
 * `.tsx` rendering test there is not collected by jest (`jest.config.ts:30`).
 */
export const isInboxHeaderCountVisible = (count: number): boolean => count > 0;

export const formatInboxHeaderCountLabel = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? "" : "s"}`;

export type InboxHeaderSummary = {
  /** `"3 unread"`, or `null` when nothing is unread. */
  unreadLabel: string | null;
  /** `"2 open tasks"`, or `null` when no task is open. */
  openTasksLabel: string | null;
  /** Both counts are 0: the header reads "You're all caught up". */
  isCaughtUp: boolean;
};

/**
 * The header's one count line (SC-3700 handoff §1), replacing the two count badges. `undefined` counts
 * (query loading or failed) return `null`: an unknown count is not "all caught up", so no line is shown.
 */
export const getInboxHeaderSummary = (
  unreadNotifications: number | undefined,
  openTasks: number | undefined,
): InboxHeaderSummary | null => {
  if (unreadNotifications === undefined || openTasks === undefined) {
    return null;
  }
  return {
    unreadLabel: isInboxHeaderCountVisible(unreadNotifications) ? `${unreadNotifications} unread` : null,
    openTasksLabel: isInboxHeaderCountVisible(openTasks) ? formatInboxHeaderCountLabel(openTasks, "open task") : null,
    isCaughtUp: !isInboxHeaderCountVisible(unreadNotifications) && !isInboxHeaderCountVisible(openTasks),
  };
};

export type InboxEmptyState = { title: string; body: string };

/**
 * Which empty state the list shows (handoff §5). `inbox.counts` is unfiltered, so a non-zero count with
 * an empty list means the filters are hiding what exists; no new query is needed to tell the two apart.
 * `undefined` counts (query loading or failed) keep the generic copy: "Nothing matches" is only claimed
 * once the counts have landed. A failed list query returns `null`: an Inbox that did not load is neither
 * empty nor filtered, so the caller shows the load error instead.
 */
export const getInboxEmptyState = (
  unreadNotifications: number | undefined,
  openTasks: number | undefined,
  listFailed: boolean,
): InboxEmptyState | null => {
  if (listFailed) {
    return null;
  }
  if (
    unreadNotifications !== undefined &&
    openTasks !== undefined &&
    (isInboxHeaderCountVisible(unreadNotifications) || isInboxHeaderCountVisible(openTasks))
  ) {
    return { title: "Nothing matches these filters", body: "Try turning off a filter above to see more tasks." };
  }
  return { title: "Your Inbox is empty", body: "New tasks and notifications will show up here." };
};
